import {
  ApiAddressLocationType,
  type CreateTestApiOrderInput,
  DestinationStopType,
  OriginStopType,
  type PackageType,
  type PaymentMethod,
  type TestApiOrderPackageInput,
} from "../../generated/graphql";

/** The address a stop is created at. Mirrors the external API's address body. */
export type AddressFormState = {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  locationType: ApiAddressLocationType | "";
};

/**
 * A commercial New York address, so an operator testing merge behavior can
 * submit without inventing address data. The real create path geocodes it
 * through Mapbox exactly as it geocodes a partner's address.
 */
export const DEFAULT_ADDRESS: AddressFormState = {
  name: "Empire State Building",
  line1: "20 W 34th St.",
  line2: "",
  city: "New York",
  state: "NY",
  zip: "10001",
  country: "USA",
  locationType: ApiAddressLocationType.Commercial,
};

/** One package row on the form. Numbers stay strings while being typed. */
export type PackageFormState = {
  /**
   * Identity of the row for as long as the form is open, so React keys a row to
   * its own inputs. Removing a row shifts every later row's index, so an index
   * key would move a half-typed value onto the wrong row. Never sent to the API.
   */
  id: string;
  description: string;
  quantity: string;
  weight: string;
  type: PackageType | "";
  barcodes: string;
};

/**
 * Generates a package row's id. Prefers `crypto.randomUUID`, falling back to a
 * timestamp-and-random id where it is unavailable (a non-secure context) so
 * adding a row never throws.
 *
 * @returns A unique row id.
 */
const makePackageRowId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `package-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** Everything the API-order tab collects. */
export type ApiOrderFormState = {
  companyUuid: string;
  billingContactUuid: string;
  serviceLevelUuid: string;
  houseAirwayBillNumber: string;
  masterAirwayBillOfLadingNumber: string;
  paymentMethod: PaymentMethod | "";
  includeOrigin: boolean;
  originStopType: OriginStopType;
  originAddress: AddressFormState;
  includeDestination: boolean;
  destinationStopType: DestinationStopType;
  destinationAddress: AddressFormState;
  packages: PackageFormState[];
};

/**
 * A one-pallet package, so an operator testing merge behavior can submit without
 * inventing package data. A function rather than a constant because every row
 * needs its own {@link PackageFormState.id}.
 *
 * @returns A new package row.
 */
export const createDefaultPackage = (): PackageFormState => ({
  id: makePackageRowId(),
  description: "Pallet of widgets",
  quantity: "1",
  weight: "100",
  type: "",
  barcodes: "",
});

/**
 * The form an operator starts from, and the one "Reset form" returns to.
 *
 * @returns A new form state.
 */
export const createEmptyApiOrderForm = (): ApiOrderFormState => ({
  companyUuid: "",
  billingContactUuid: "",
  serviceLevelUuid: "",
  houseAirwayBillNumber: "",
  masterAirwayBillOfLadingNumber: "",
  paymentMethod: "",
  includeOrigin: false,
  originStopType: OriginStopType.Pickup,
  originAddress: DEFAULT_ADDRESS,
  includeDestination: true,
  destinationStopType: DestinationStopType.Delivery,
  destinationAddress: DEFAULT_ADDRESS,
  packages: [createDefaultPackage()],
});

/** Trims a text input and maps a blank to null, which the API treats as unset. */
const orNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Parses a numeric text input, mapping a blank or unparseable value to null so
 * the API stores no number rather than a NaN.
 */
const numberOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const toAddressInput = (address: AddressFormState) => ({
  name: address.name.trim(),
  line1: address.line1.trim(),
  line2: orNull(address.line2),
  city: address.city.trim(),
  state: address.state.trim(),
  zip: address.zip.trim(),
  country: address.country.trim(),
  locationType: address.locationType === "" ? null : address.locationType,
});

const toPackageInput = (
  packageForm: PackageFormState,
): TestApiOrderPackageInput => {
  const barcodes = packageForm.barcodes
    .split(",")
    .map((barcode) => barcode.trim())
    .filter((barcode) => barcode !== "");
  return {
    description: packageForm.description.trim(),
    quantity: numberOrNull(packageForm.quantity) ?? 1,
    weight: numberOrNull(packageForm.weight),
    type: packageForm.type === "" ? null : packageForm.type,
    barcodes: barcodes.length === 0 ? null : barcodes,
  };
};

/**
 * Names the first reason the form cannot be submitted, so the operator sees one
 * concrete problem rather than a server-side validation dump.
 *
 * @param form The current form state.
 * @returns The problem, or null when the form is submittable.
 */
export const validateApiOrderForm = (
  form: ApiOrderFormState,
): string | null => {
  if (form.companyUuid === "") {
    return "Pick a company.";
  }
  if (form.billingContactUuid === "") {
    return "Pick a billing contact.";
  }
  if (form.houseAirwayBillNumber.trim() === "") {
    return "Enter a HAWB.";
  }
  if (!form.includeOrigin && !form.includeDestination) {
    return "Include an origin stop, a destination stop, or both.";
  }
  if (form.packages.length === 0) {
    return "Add at least one package.";
  }
  if (
    form.packages.some((packageForm) => packageForm.description.trim() === "")
  ) {
    return "Every package needs a description.";
  }
  return null;
};

/**
 * Builds the mutation input from the form.
 *
 * Assumes {@link validateApiOrderForm} already returned null; it does not
 * re-check the required fields.
 *
 * @param form The current form state.
 * @returns The `createTestApiOrder` input.
 */
export const toCreateTestApiOrderInput = (
  form: ApiOrderFormState,
): CreateTestApiOrderInput => ({
  companyUuid: form.companyUuid,
  billingContactUuid: form.billingContactUuid,
  serviceLevelUuid: orNull(form.serviceLevelUuid),
  houseAirwayBillNumber: form.houseAirwayBillNumber.trim(),
  masterAirwayBillOfLadingNumber: orNull(form.masterAirwayBillOfLadingNumber),
  paymentMethod: form.paymentMethod === "" ? null : form.paymentMethod,
  packages: form.packages.map((packageForm) => toPackageInput(packageForm)),
  origin: form.includeOrigin
    ? {
        originStopType: form.originStopType,
        address: toAddressInput(form.originAddress),
      }
    : null,
  destination: form.includeDestination
    ? {
        destinationStopType: form.destinationStopType,
        address: toAddressInput(form.destinationAddress),
      }
    : null,
});
